import { produce } from "immer";

import type WebApiClient from "../../api/WebApiClient.js";
import { createErrorHandler } from "../../app-utils/errors.js";
import { type InputTableQuerySpec, type Organism, type RelatedProject } from "../../graphql.js";
import {
  LOAD_GENOME_FAILURE,
  LOAD_GENOME_GFF_FEATURE_STATISTICS_FAILURE,
  LOAD_GENOME_GFF_FEATURE_STATISTICS_REQUEST,
  LOAD_GENOME_GFF_FEATURE_STATISTICS_SUCCESS,
  LOAD_GENOME_RELATED_PROJECTS_FAILURE,
  LOAD_GENOME_RELATED_PROJECTS_REQUEST,
  LOAD_GENOME_RELATED_PROJECTS_SUCCESS,
  LOAD_GENOME_REQUEST,
  LOAD_GENOME_SUCCESS,
  RESET_GENOME,
  UPDATE_LAST_OPEN_NUCLEOTIDE_FEATURE_TABLE_SPEC,
} from "../action-types.js";
import { type IAppState } from "../reducer.js";
import { type ReduxAction, createReducer } from "../redux-utils.js";
import { getProjectLabel } from "./config.js";

/**
 * State definition
 */
export interface IGenomeState {
  genome?: Organism;
  isLoading: boolean;
  relatedProjects: RelatedProject[];
  isLoadingRelatedProjects: boolean;
  isLoadingGffFeatureStatistics: boolean;
  lastOpenFeatureTableSpec: InputTableQuerySpec;
}

const initialState: IGenomeState = {
  isLoading: false,
  isLoadingRelatedProjects: false,
  isLoadingGffFeatureStatistics: false,
  relatedProjects: null,
  genome: null,
  lastOpenFeatureTableSpec: null,
};

/**
 * Reducers
 */
export default createReducer(initialState, {
  [RESET_GENOME]: (state: IGenomeState): IGenomeState => {
    return { ...state, genome: null };
  },

  [LOAD_GENOME_REQUEST]: (state: IGenomeState, _payload: Organism): IGenomeState => {
    return {
      ...state,
      isLoading: true,
    };
  },
  [LOAD_GENOME_SUCCESS]: (state: IGenomeState, payload: Organism): IGenomeState => {
    return {
      ...state,
      isLoading: false,
      genome: payload,
    };
  },
  [LOAD_GENOME_FAILURE]: (state: IGenomeState, _payload): IGenomeState => {
    return {
      ...state,
      //isLoading: false, //  make sure the downstream summary pages are not loaded further, creating a loop of failing queries to download the entity (TODO use a different mechanism)
      genome: null,
    };
  },

  [LOAD_GENOME_RELATED_PROJECTS_REQUEST]: (state: IGenomeState, _payload: Organism): IGenomeState => {
    return {
      ...state,
      isLoadingRelatedProjects: true,
      relatedProjects: [], // make sure the spinner is shown and reset list from previous genome
    };
  },
  [LOAD_GENOME_RELATED_PROJECTS_SUCCESS]: (state: IGenomeState, payload: Organism): IGenomeState => {
    if (state.genome && state.genome.id === payload.id) {
      return {
        ...state,
        isLoadingRelatedProjects: false,
        relatedProjects: payload.relatedProjects,
      };
    } else {
      return state;
    }
  },
  [LOAD_GENOME_RELATED_PROJECTS_FAILURE]: (state: IGenomeState, payload: Organism): IGenomeState => {
    if (state.genome.id === payload.id) {
      return {
        ...state,
        isLoadingRelatedProjects: false,
      };
    } else {
      return state;
    }
  },
  [LOAD_GENOME_GFF_FEATURE_STATISTICS_REQUEST]: (state: IGenomeState, _payload: Organism): IGenomeState => {
    return {
      ...state,
      isLoadingGffFeatureStatistics: true,
    };
  },
  [LOAD_GENOME_GFF_FEATURE_STATISTICS_SUCCESS]: (state: IGenomeState, payload: Organism): IGenomeState => {
    if (state.genome && state.genome.id === payload.id) {
      return produce(state, (draft) => {
        draft.isLoadingGffFeatureStatistics = false;
        draft.genome.genomeStatistics = { ...draft.genome.genomeStatistics, ...payload.genomeStatistics };
      });
    } else {
      return state;
    }
  },
  [LOAD_GENOME_GFF_FEATURE_STATISTICS_FAILURE]: (state: IGenomeState, payload: Organism): IGenomeState => {
    if (state.genome.id === payload.id) {
      return {
        ...state,
        isLoadingGffFeatureStatistics: false,
      };
    } else {
      return state;
    }
  },
  [UPDATE_LAST_OPEN_NUCLEOTIDE_FEATURE_TABLE_SPEC]: (
    state: IGenomeState,
    payload: InputTableQuerySpec,
  ): IGenomeState => {
    return {
      ...state,
      lastOpenFeatureTableSpec: payload,
    };
  },
});

/**
 * Action creators
 */
const pendingPromisesLookup: Record<number, Promise<Organism>> = {};
let lastFetchedGenomeId = -1;

export function shouldLoadGenome(genomeId: number): ReduxAction<Promise<Organism>> {
  return (dispatch, getState, webApiClient) => {
    const state: IAppState = getState();
    lastFetchedGenomeId = genomeId;

    // return already existing promise corresponding to the same view request
    if (pendingPromisesLookup[genomeId]) {
      return pendingPromisesLookup[genomeId];
    }

    // fetch the genome only if genome is either absent or doesn't match the genome ID
    if (state.genome.genome && state.genome.genome.id === genomeId) {
      return Promise.resolve(state.genome.genome);
    }

    dispatch({ type: LOAD_GENOME_REQUEST });

    pendingPromisesLookup[genomeId] = webApiClient
      .fetchSummaryGenome(genomeId)
      .then(
        (response: Organism) => {
          delete pendingPromisesLookup[genomeId];

          if (lastFetchedGenomeId === response.id) {
            dispatch({
              type: LOAD_GENOME_SUCCESS,
              payload: response,
            });

            // load the related projects only after successful loading of the genome,
            // otherwise the project data is not stored to the redux state
            dispatch(loadGenomeRelatedProjects(genomeId)); // TODO should be fetched for summary page only

            return response;
          }

          return state.genome.genome;
        },
        (rejected) => {
          delete pendingPromisesLookup[genomeId];
          throw rejected;
        },
      )
      .catch(
        createErrorHandler(dispatch, {
          actionType: LOAD_GENOME_FAILURE,
          alertTitle: "Fetching of Genome Failed",
        }),
      );
    return pendingPromisesLookup[genomeId];
  };
}

export function loadGenomeRelatedProjects(genomeId: number) {
  return (dispatch, _getState, webApiClient: WebApiClient): Promise<Organism> => {
    dispatch({ type: LOAD_GENOME_RELATED_PROJECTS_REQUEST });

    return webApiClient
      .fetchGenomeRelatedProjects(genomeId)
      .then((response: Organism) => {
        dispatch({
          type: LOAD_GENOME_RELATED_PROJECTS_SUCCESS,
          payload: response,
        });

        return response;
      })
      .catch(
        createErrorHandler(dispatch, {
          actionType: LOAD_GENOME_RELATED_PROJECTS_FAILURE,
          actionPayload: { id: genomeId },
          alertTitle: `Fetching of Related ${getProjectLabel(false, true)} of Genome Failed`,
        }),
      );
  };
}

export function loadGenomeGffFeatureStatistics(genomeId: number) {
  return (dispatch, _getState, webApiClient: WebApiClient): Promise<Organism> => {
    dispatch({ type: LOAD_GENOME_GFF_FEATURE_STATISTICS_REQUEST });

    return webApiClient
      .fetchGenomeGffFeatureStatistics(genomeId)
      .then((response: Organism) => {
        dispatch({
          type: LOAD_GENOME_GFF_FEATURE_STATISTICS_SUCCESS,
          payload: response,
        });
        return response;
      })
      .catch(
        createErrorHandler(dispatch, {
          actionType: LOAD_GENOME_GFF_FEATURE_STATISTICS_FAILURE,
          alertTitle: `Fetching of GFF Feature Statistics of Genome Failed`,
        }),
      );
  };
}

export function checkSequencedOrganisms(tableQuerySpec: InputTableQuerySpec) {
  return (_dispatch, _getState, webApiClient: WebApiClient) => {
    return webApiClient.checkSequencedOrganisms(tableQuerySpec);
  };
}

export function updateLastOpenNucleotideFeatureTableSpec(tableSpec: InputTableQuerySpec) {
  return (dispatch, _getState) => {
    dispatch({ type: UPDATE_LAST_OPEN_NUCLEOTIDE_FEATURE_TABLE_SPEC, payload: tableSpec });
  };
}
